import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
  Table,
  Badge,
  Modal,
  OverlayTrigger,
  Tooltip,
  ProgressBar,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaDownload,
  FaCheck,
  FaTimes,
  FaThumbsUp,
  FaCalendarAlt,
  FaUsers,
  FaClipboardList,
  FaFileUpload,
  FaFilter,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getFirestore,
  serverTimestamp,
  setDoc,
  getDoc,
  writeBatch,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import escribirLog from "../../functions/escribirLog";

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

const OcurrenciasMes = ({ usuario }) => {
  const [ocurrencias, setOcurrencias] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOcurrencia, setEditingOcurrencia] = useState(null);
  const [filtroPeriodo, setFiltroPeriodo] = useState("");
  const [filtroEmpleado, setFiltroEmpleado] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [formData, setFormData] = useState({
    empleado_id: "",
    tipo: "",
    fecha: "",
    dias_afectados: 1,
    horas_afectadas: 8,
    minutos_tardanza: 0,
    con_goce: false,
    justificado: false,
    motivo: "",
    observaciones: "",
    periodo: "",
    activo: false,
  });
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("");

  // Prevenir cierre accidental durante la carga masiva
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (importing) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [importing]);

  const tiposOcurrencia = [
    { value: "falta", label: "Falta" },
    { value: "vacaciones", label: "Vacaciones" },
    { value: "descanso_medico", label: "Descanso Médico" },
    { value: "licencia_con_goce", label: "Licencia con Goce" },
    { value: "licencia_sin_goce", label: "Licencia sin Goce" },
    { value: "tardanza", label: "Tardanza" },
    { value: "permiso", label: "Permiso" },
    { value: "he_25", label: "Horas Extras 25%" },
    { value: "he_35", label: "Horas Extras 35%" },
    { value: "he_100", label: "Horas Extras 100%" },
    { value: "otro", label: "Otro" },
  ];

  useEffect(() => {
    cargarEmpleados();
    cargarOcurrencias();
  }, [usuario]);

  const cargarEmpleados = async () => {
    try {
      const empleadosRef = collection(db, "pl_empleados");
      const q = query(empleadosRef, where("ruc_empresa", "==", usuario.ruc));
      const empleadosSnap = await getDocs(q);
      const empleadosData = empleadosSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setEmpleados(empleadosData);
    } catch (error) {
      console.error("Error cargando empleados:", error);
      mostrarError("No se pudieron cargar los empleados");
    }
  };

  const cargarOcurrencias = async () => {
    try {
      const ocurrenciasRef = collection(db, "pl_ocurrencias");
      const q = query(
        ocurrenciasRef,
        where("ruc_empresa", "==", usuario.ruc),
        orderBy("fecha_creacion", "desc")
      );
      const ocurrenciasSnap = await getDocs(q);
      const ocurrenciasData = ocurrenciasSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setOcurrencias(ocurrenciasData);
      setLoading(false);
    } catch (error) {
      console.error("Error cargando ocurrencias:", error);
      mostrarError("No se pudieron cargar las ocurrencias");
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleDiasChange = (e) => {
    const dias = parseFloat(e.target.value) || 0;
    const horas = dias * 8;
    setFormData({
      ...formData,
      dias_afectados: dias,
      horas_afectadas: horas,
    });
  };

  const handleHorasChange = (e) => {
    const horas = parseFloat(e.target.value) || 0;
    const dias = horas / 8;
    setFormData({
      ...formData,
      horas_afectadas: horas,
      dias_afectados: dias,
    });
  };

  const handleFechaChange = (e) => {
    const fecha = e.target.value;
    const periodo = fecha ? fecha.substring(0, 7) : "";
    setFormData({
      ...formData,
      fecha,
      periodo,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones adicionales
    if (!formData.empleado_id) {
      mostrarError("Debe seleccionar un empleado");
      return;
    }
    if (!formData.tipo) {
      mostrarError("Debe seleccionar un tipo de ocurrencia");
      return;
    }
    if (!formData.fecha) {
      mostrarError("Debe seleccionar una fecha");
      return;
    }
    if (formData.dias_afectados < 0 || formData.horas_afectadas < 0) {
      mostrarError("Los días y horas afectados no pueden ser negativos");
      return;
    }
    // Verificar consistencia: días * 8 ≈ horas
    const expectedHoras = formData.dias_afectados * 8;
    if (Math.abs(formData.horas_afectadas - expectedHoras) > 0.1) {
      mostrarError("Los días y horas no son consistentes. Verifique los valores.");
      return;
    }

    try {
      const customId = `${formData.empleado_id}_${formData.periodo}_${formData.tipo}_${formData.fecha}`;

      // Verificar si ya existe
      const existingDoc = await getDoc(doc(db, "pl_ocurrencias", customId));
      if (existingDoc.exists() && !editingOcurrencia) {
        mostrarError("Ya existe una ocurrencia con los mismos datos. Edite la existente o cambie la fecha.");
        return;
      }

      const ocurrenciaData = {
        ...formData,
        ruc_empresa: usuario.ruc,
        creado_por: usuario.email || "usuario_admin",
        estado: "manual",
        activo: formData.activo,
        fecha_creacion: serverTimestamp(),
      };

      if (editingOcurrencia) {
        await updateDoc(
          doc(db, "pl_ocurrencias", editingOcurrencia.id),
          ocurrenciaData
        );
        await escribirLog(
          "Actualización de Ocurrencia Manual",
          `Empleado: ${formData.empleado_id}, Tipo: ${formData.tipo}, Fecha: ${formData.fecha}`,
          usuario.email || "admin"
        );
        mostrarExito("Ocurrencia actualizada correctamente");
      } else {
        await setDoc(doc(db, "pl_ocurrencias", customId), ocurrenciaData);
        await escribirLog(
          "Creación de Ocurrencia Manual",
          `Empleado: ${formData.empleado_id}, Tipo: ${formData.tipo}, Fecha: ${formData.fecha}`,
          usuario.email || "admin"
        );
        mostrarExito("Ocurrencia agregada correctamente");
      }

      setShowModal(false);
      setEditingOcurrencia(null);
      resetForm();
      cargarOcurrencias();
    } catch (error) {
      console.error("Error guardando ocurrencia:", error);
      mostrarError("No se pudo guardar la ocurrencia");
    }
  };

  const resetForm = () => {
    setFormData({
      empleado_id: "",
      tipo: "",
      fecha: "",
      dias_afectados: 1,
      horas_afectadas: 8, // 1 día * 8 horas
      minutos_tardanza: 0,
      con_goce: false,
      justificado: false,
      motivo: "",
      observaciones: "",
      periodo: "",
      activo: false,
    });
  };

  const handleAprobar = async (ocurrencia) => {
    const result = await Swal.fire({
      title: "¿Aprobar ocurrencia?",
      text: `¿Estás seguro de aprobar la ocurrencia de ${ocurrencia.tipo} para el empleado?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#8B4513",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, aprobar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await updateDoc(doc(db, "pl_ocurrencias", ocurrencia.id), {
          activo: true,
          fecha_aprobacion: serverTimestamp(),
          aprobado_por: usuario.email || "usuario_admin",
        });
        await escribirLog(
          "Aprobación de Ocurrencia",
          `ID: ${ocurrencia.id}, Empleado: ${ocurrencia.empleado_id}`,
          usuario.email || "admin"
        );
        cargarOcurrencias();
      } catch (error) {
        console.error("Error aprobando ocurrencia:", error);
        mostrarError("No se pudo aprobar la ocurrencia");
      }
    }
  };

  const handleAprobarLote = async () => {
    const filteredOcurrencias = ocurrencias.filter((o) => {
      return (
        !o.activo &&
        (filtroPeriodo === "" || o.periodo === filtroPeriodo) &&
        (filtroEmpleado === "" || o.empleado_id === filtroEmpleado)
      );
    });

    if (filteredOcurrencias.length === 0) {
      Swal.fire("Información", "No hay ocurrencias pendientes que coincidan con los filtros actuales.", "info");
      return;
    }

    const result = await Swal.fire({
      title: "¿Aprobar en lote?",
      text: `Se aprobarán ${filteredOcurrencias.length} ocurrencias pendientes para los filtros seleccionados.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, aprobar todo",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const batch = writeBatch(db);
        filteredOcurrencias.forEach((occ) => {
          const docRef = doc(db, "pl_ocurrencias", occ.id);
          batch.update(docRef, {
            activo: true,
            fecha_aprobacion: serverTimestamp(),
            aprobado_por: usuario.email || "admin",
          });
        });

        await batch.commit();

        await escribirLog(
          "Aprobación Masiva de Ocurrencias",
          `Cantidad: ${filteredOcurrencias.length}, Filtros: P:${filtroPeriodo}, E:${filtroEmpleado}`,
          usuario.email || "admin"
        );

        mostrarExito(`${filteredOcurrencias.length} ocurrencias aprobadas correctamente`);
        cargarOcurrencias();
      } catch (error) {
        console.error("Error en aprobación masiva:", error);
        mostrarError("Ocurrió un error al procesar la aprobación masiva");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (ocurrencia) => {
    setEditingOcurrencia(ocurrencia);
    setFormData({
      empleado_id: ocurrencia.empleado_id,
      tipo: ocurrencia.tipo,
      fecha: ocurrencia.fecha,
      dias_afectados: ocurrencia.dias_afectados,
      horas_afectadas: ocurrencia.horas_afectadas,
      minutos_tardanza: ocurrencia.minutos_tardanza,
      con_goce: ocurrencia.con_goce,
      justificado: ocurrencia.justificado,
      motivo: ocurrencia.motivo,
      observaciones: ocurrencia.observaciones,
      periodo: ocurrencia.periodo,
      activo: ocurrencia.activo || false,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#8B4513",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, "pl_ocurrencias", id));
        await escribirLog(
          "Eliminación de Ocurrencia",
          `ID: ${id}`,
          usuario.email || "admin"
        );
        mostrarExito("La ocurrencia ha sido eliminada");
        cargarOcurrencias();
      } catch (error) {
        console.error("Error eliminando ocurrencia:", error);
        mostrarError("No se pudo eliminar la ocurrencia");
      }
    }
  };

  const getTipoBadge = (tipo) => {
    const colores = {
      falta: "danger",
      vacaciones: "success",
      descanso_medico: "warning",
      licencia_con_goce: "info",
      licencia_sin_goce: "secondary",
      tardanza: "warning",
      permiso: "primary",
      he_25: "info",
      he_35: "primary",
      he_100: "success",
      otro: "dark",
    };
    return (
      <Badge bg={colores[tipo] || "secondary"} className="px-2 py-1">
        {tipo.replace("_", " ").toUpperCase()}
      </Badge>
    );
  };

  const exportToExcel = () => {
    const filteredOcurrencias = ocurrencias.filter((ocurrencia) => {
      return (
        (filtroPeriodo === "" || ocurrencia.periodo === filtroPeriodo) &&
        (filtroEmpleado === "" || ocurrencia.empleado_id === filtroEmpleado)
      );
    });

    const data = filteredOcurrencias.map((ocurrencia) => {
      const empleado = empleados.find((e) => e.id === ocurrencia.empleado_id);
      return {
        DNI: ocurrencia.empleado_id,
        Empleado: empleado ? `${empleado.apellidos} ${empleado.nombres}` : "",
        Tipo: ocurrencia.tipo,
        Fecha: ocurrencia.fecha,
        "Días Afectados": ocurrencia.dias_afectados,
        "Horas Afectadas": ocurrencia.horas_afectadas,
        "Minutos Tardanza": ocurrencia.minutos_tardanza,
        "Con Goce": ocurrencia.con_goce ? "Sí" : "No",
        Justificado: ocurrencia.justificado ? "Sí" : "No",
        Motivo: ocurrencia.motivo,
        Observaciones: ocurrencia.observaciones,
        Período: ocurrencia.periodo,
        Activo: ocurrencia.activo ? "Sí" : "No",
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ocurrencias");
    XLSX.writeFile(wb, `ocurrencias_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const downloadTemplate = () => {
    // Definimos la estructura maestra con ejemplos para guiar al usuario
    const templateData = [
      {
        DNI: "10000001",
        Empleado: "EJEMPLO FALTA",
        Fecha: "2026-01-01",
        Tipo: "falta",
        "Días Afectados": 1,
        "Horas Afectadas": 8,
        "Minutos Tardanza": 0,
        "Con Goce": "No",
        Justificado: "No",
        Motivo: "Inasistencia injustificada",
        Observaciones: "",
        Activo: "Sí",
        "Horas 25%": 0,
        "Horas 35%": 0,
        "Horas 100%": 0,
      },
      {
        DNI: "10000002",
        Empleado: "EJEMPLO VACACIONES",
        Fecha: "2026-01-02",
        Tipo: "vacaciones",
        "Días Afectados": 7,
        "Horas Afectadas": 56,
        "Minutos Tardanza": 0,
        "Con Goce": "Sí",
        Justificado: "Sí",
        Motivo: "Vacaciones programadas",
        Observaciones: "",
        Activo: "Sí",
        "Horas 25%": 0,
        "Horas 35%": 0,
        "Horas 100%": 0,
      },
      {
        DNI: "10000003",
        Empleado: "EJEMPLO DESCANSO MEDICO",
        Fecha: "2026-01-03",
        Tipo: "descanso_medico",
        "Días Afectados": 3,
        "Horas Afectadas": 24,
        "Minutos Tardanza": 0,
        "Con Goce": "Sí",
        Justificado: "Sí",
        Motivo: "Certificado médico",
        Observaciones: "",
        Activo: "Sí",
        "Horas 25%": 0,
        "Horas 35%": 0,
        "Horas 100%": 0,
      },
      {
        DNI: "10000004",
        Empleado: "EJEMPLO LICENCIA C/G",
        Fecha: "2026-01-04",
        Tipo: "licencia_con_goce",
        "Días Afectados": 1,
        "Horas Afectadas": 8,
        "Minutos Tardanza": 0,
        "Con Goce": "Sí",
        Justificado: "Sí",
        Motivo: "Fallecimiento familiar",
        Observaciones: "",
        Activo: "Sí",
        "Horas 25%": 0,
        "Horas 35%": 0,
        "Horas 100%": 0,
      },
      {
        DNI: "10000005",
        Empleado: "EJEMPLO LICENCIA S/G",
        Fecha: "2026-01-05",
        Tipo: "licencia_sin_goce",
        "Días Afectados": 2,
        "Horas Afectadas": 16,
        "Minutos Tardanza": 0,
        "Con Goce": "No",
        Justificado: "No",
        Motivo: "Permiso personal",
        Observaciones: "",
        Activo: "Sí",
        "Horas 25%": 0,
        "Horas 35%": 0,
        "Horas 100%": 0,
      },
      {
        DNI: "10000006",
        Empleado: "EJEMPLO TARDANZA",
        Fecha: "2026-01-06",
        Tipo: "tardanza",
        "Días Afectados": 0,
        "Horas Afectadas": 0,
        "Minutos Tardanza": 45,
        "Con Goce": "No",
        Justificado: "No",
        Motivo: "Retraso ingreso",
        Observaciones: "",
        Activo: "Sí",
        "Horas 25%": 0,
        "Horas 35%": 0,
        "Horas 100%": 0,
      },
      {
        DNI: "10000007",
        Empleado: "EJEMPLO PERMISO",
        Fecha: "2026-01-07",
        Tipo: "permiso",
        "Días Afectados": 0,
        "Horas Afectadas": 2,
        "Minutos Tardanza": 0,
        "Con Goce": "Sí",
        Justificado: "Sí",
        Motivo: "Cita ESSALUD",
        Observaciones: "",
        Activo: "Sí",
        "Horas 25%": 0,
        "Horas 35%": 0,
        "Horas 100%": 0,
      },
      {
        DNI: "10000008",
        Empleado: "EJEMPLO HE COLUMNAS",
        Fecha: "2026-01-08",
        Tipo: "",
        "Días Afectados": 0,
        "Horas Afectadas": 0,
        "Minutos Tardanza": 0,
        "Con Goce": "Sí",
        Justificado: "Sí",
        Motivo: "Sobretiempo",
        Observaciones: "Ejemplo usando columnas HE",
        Activo: "Sí",
        "Horas 25%": 2,
        "Horas 35%": 1,
        "Horas 100%": 0,
      },
      {
        DNI: "10000009",
        Empleado: "EJEMPLO OTRO",
        Fecha: "2026-01-09",
        Tipo: "otro",
        "Días Afectados": 0,
        "Horas Afectadas": 0,
        "Minutos Tardanza": 0,
        "Con Goce": "Sí",
        Justificado: "Sí",
        Motivo: "Comisión de servicio",
        Observaciones: "",
        Activo: "Sí",
        "Horas 25%": 0,
        "Horas 35%": 0,
        "Horas 100%": 0,
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);

    // Ajustar anchos de columna para mejor legibilidad
    const wscols = [
      { wch: 10 }, // DNI
      { wch: 25 }, // Empleado
      { wch: 12 }, // Fecha
      { wch: 15 }, // Tipo
      { wch: 15 }, // Días
      { wch: 15 }, // Horas
      { wch: 15 }, // Minutos
      { wch: 10 }, // Con Goce
      { wch: 10 }, // Justificado
      { wch: 20 }, // Motivo
      { wch: 20 }, // Observaciones
      { wch: 10 }, // Activo
      { wch: 12 }, // HE 25
      { wch: 12 }, // HE 35
      { wch: 12 }, // HE 100
    ];
    ws["!cols"] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla Ocurrencias");

    // Crear una hoja de ayuda con los tipos válidos
    const helpData = [
      { "Campo": "Tipo (Valores Válidos)", "Descripción": "falta, vacaciones, descanso_medico, licencia_con_goce, licencia_sin_goce, tardanza, permiso, he_25, he_35, he_100, otro" },
      { "Campo": "Fecha", "Descripción": "Formato AAAA-MM-DD (ej: 2026-01-20)" },
      { "Campo": "DNI", "Descripción": "8 o 9 dígitos numéricos" },
      { "Campo": "Con Goce / Justificado", "Descripción": "Usar 'Sí' o 'No'" },
      { "Campo": "Columnas HE", "Descripción": "Las columnas 'Horas 25%', etc. se pueden usar en lugar del campo 'Tipo' para cargar HE directamente." }
    ];
    const wsHelp = XLSX.utils.json_to_sheet(helpData);
    XLSX.utils.book_append_sheet(wb, wsHelp, "AYUDA - INSTRUCCIONES");

    XLSX.writeFile(wb, "Plantilla_Maestra_Ocurrencias.xlsx");
  };

  const importFromExcel = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportProgress(0);
    setImportStatus("Leyendo archivo...");

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { 
          type: "array",
          cellDates: true,
          cellText: false
        });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        const errors = [];
        const successes = [];
        const totalRows = jsonData.length;

        const getVal = (row, ...names) => {
          const keys = Object.keys(row);
          const match = keys.find(k => names.some(n => k.toLowerCase().trim() === n.toLowerCase().trim()));
          return match !== undefined ? row[match] : undefined;
        };

        const cleanDNI = (val) => {
          if (val === undefined || val === null) return "";
          const s = val.toString().trim().replace(/[^0-9]/g, "");
          return s.padStart(8, "0");
        };

        const formatDate = (val) => {
          if (!val) return "";
          if (val instanceof Date) {
            return val.toISOString().split('T')[0];
          }
          const s = val.toString().trim();
          if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
          return s;
        };

        if (jsonData.length > 0) {
          console.log("📊 [OcurrenciasMes] Columnas detectadas:", Object.keys(jsonData[0]));
        }

        for (let i = 0; i < jsonData.length; i++) {
          const row = jsonData[i];
          const currentRow = i + 1;
          setImportProgress(Math.round(((i + 1) / totalRows) * 100));
          setImportStatus(`Procesando fila ${currentRow} de ${totalRows}...`);

          try {
            const dniRaw = getVal(row, "DNI", "dni", "Documento", "IDENTIDAD");
            const dni = cleanDNI(dniRaw);

            if (!dni || dni === "00000000") {
              if (dniRaw) {
                console.warn(`⚠️ [Fila ${currentRow}] DNI no válido: "${dniRaw}"`);
                errors.push(`Fila ${currentRow}: DNI no válido`);
              }
              continue;
            }

            const empleado = empleados.find((e) => cleanDNI(e.id || e.dni) === dni);
            if (!empleado) {
              console.warn(`❌ [Fila ${currentRow}] Empleado no encontrado para DNI "${dni}"`);
              errors.push(`Fila ${currentRow}: Empleado con DNI ${dni} no encontrado en sistema`);
              continue;
            }

            const fechaRaw = getVal(row, "Fecha", "fecha", "DATE", "dia");
            const fecha = formatDate(fechaRaw);
            if (!fecha) {
              console.warn(`⚠️ [Fila ${currentRow}] Fecha faltante o inválida: "${fechaRaw}"`);
              errors.push(`Fila ${currentRow}: Fecha es obligatoria`);
              continue;
            }

            console.log(`🔍 [Fila ${currentRow}] Procesando: DNI ${dni}, Fecha ${fecha}`);

            const periodo = fecha.substring(0, 7);
            const ruc_empresa = usuario.ruc;
            const creado_por = usuario.email || "usuario_admin";
            const fecha_creacion = serverTimestamp();

            const ocurrenciasParaCrear = [];

            // 1. Verificar tipo explícito
            const tipoExcel = getVal(row, "Tipo", "tipo", "incidencia");
            if (tipoExcel) {
              const tipoNormalized = tipoExcel.toString().toLowerCase().trim();
              const rawQty = getVal(row, "Días/Horas", "Cantidad", "Valor");
              let dias_afectados = parseFloat(getVal(row, "Días Afectados", "dias", "cantidad_dias")) || 0;
              let horas_afectadas = parseFloat(getVal(row, "Horas Afectadas", "horas", "cantidad_horas")) || 0;
              
              const minutos_tardanza = parseInt(getVal(row, "Minutos Tardanza", "minutos", "tardanza_minutos")) || 0;

              // Si no se encontraron valores en columnas específicas, intentar con la genérica
              if (dias_afectados === 0 && horas_afectadas === 0 && rawQty) {
                const rawStr = rawQty.toString().toLowerCase();
                const num = parseFloat(rawQty) || 0;
                if (rawStr.includes("día") || rawStr.includes("dia")) {
                  dias_afectados = num;
                } else if (rawStr.includes("hr") || rawStr.includes("ora")) {
                  horas_afectadas = num;
                } else {
                  // Fallback: si es HE, asumimos horas. Si es vacaciones/falta, dias.
                  const esHE = ["he_25", "he_35", "he_100"].includes(tipoNormalized);
                  if (esHE) horas_afectadas = num;
                  else dias_afectados = num;
                }
              }

              const esHE = ["he_25", "he_35", "he_100"].includes(tipoNormalized);
              
              const goceRaw = getVal(row, "Con Goce", "goce", "con_goce");
              const justifRaw = getVal(row, "Justificado", "justificado", "justifica");

              // EVITAR DUPLICADOS: Solo agregar si NO es HE o si siendo HE tiene cantidad
              if (!esHE || (horas_afectadas > 0 || dias_afectados > 0)) {
                if (!esHE) {
                  if (dias_afectados > 0 && horas_afectadas === 0) {
                    horas_afectadas = dias_afectados * 8;
                  } else if (horas_afectadas > 0 && dias_afectados === 0) {
                    dias_afectados = horas_afectadas / 8;
                  }
                } else {
                  dias_afectados = 0;
                }

                ocurrenciasParaCrear.push({
                  tipo: tipoNormalized,
                  dias_afectados,
                  horas_afectadas,
                  minutos_tardanza,
                  con_goce: goceRaw === "Sí" || goceRaw === "SI" || goceRaw === true,
                  justificado: justifRaw === "Sí" || justifRaw === "SI" || justifRaw === true,
                  motivo: getVal(row, "Motivo", "motivo", "glosa") || "Carga Masiva",
                  observaciones: getVal(row, "Observaciones", "observacion", "obs") || "",
                  activo: false,
                  customSuffix: ""
                });
              }
            }

            // 2. Horas Extras por columnas
            const checkHE = (namesOriginal, type, suffix) => {
              const valRaw = getVal(row, ...namesOriginal);
              if (valRaw && parseFloat(valRaw) > 0) {
                ocurrenciasParaCrear.push({
                  tipo: type,
                  dias_afectados: 0,
                  horas_afectadas: parseFloat(valRaw),
                  minutos_tardanza: 0,
                  con_goce: true,
                  justificado: true,
                  motivo: `Horas Extras ${type} (Importado)`,
                  observaciones: getVal(row, "Observaciones", "observacion", "obs") || "",
                  activo: false,
                  customSuffix: suffix
                });
              }
            };

            checkHE(["Horas 25%", "HE 25%", "25%", "he25"], "he_25", "_he25");
            checkHE(["Horas 35%", "HE 35%", "35%", "he35"], "he_35", "_he35");
            checkHE(["Horas 100%", "HE 100%", "100%", "he100"], "he_100", "_he100");

            for (const occ of ocurrenciasParaCrear) {
              const customId = `${dni}_${periodo}_${occ.tipo}_${fecha}${occ.customSuffix}`;
              const ocurrenciaData = {
                empleado_id: dni,
                tipo: occ.tipo,
                fecha,
                dias_afectados: occ.dias_afectados,
                horas_afectadas: occ.horas_afectadas,
                minutos_tardanza: occ.minutos_tardanza,
                con_goce: occ.con_goce,
                justificado: occ.justificado,
                motivo: occ.motivo,
                observaciones: occ.observaciones,
                periodo,
                ruc_empresa,
                creado_por,
                estado: "manual",
                activo: occ.activo,
                fecha_creacion
              };

              await setDoc(doc(db, "pl_ocurrencias", customId), ocurrenciaData);
              successes.push(`${dni} - ${occ.tipo}`);
            }
          } catch (error) {
            errors.push(`Fila ${currentRow}: ${error.message}`);
          }

          // Pequeña pausa opcional cada X filas para no saturar si es necesario
          // if (i % 20 === 0) await new Promise(r => setTimeout(r, 100));
        }

        await escribirLog(
          "Carga Masiva de Ocurrencias",
          `Éxitos: ${successes.length}, Errores: ${errors.length}`,
          usuario.email || "admin"
        );

        if (errors.length > 0) {
          Swal.fire({
            title: "Importación Completada con Observaciones",
            html: `
              <div class="text-start" style="max-height: 300px, overflow-top: auto">
                <p class="text-success fw-bold">Éxitos: ${successes.length}</p>
                <p class="text-danger fw-bold">Errores: ${errors.length}</p>
                <ul class="small text-muted mb-0">
                  ${errors.slice(0, 10).map(e => `<li>${e}</li>`).join("")}
                  ${errors.length > 10 ? `<li>... y ${errors.length - 10} errores más</li>` : ""}
                </ul>
              </div>
            `,
            icon: "warning"
          });
        } else {
          mostrarExito(`${successes.length} ocurrencias importadas correctamente`);
        }
      } catch (err) {
        console.error("Error crítico en importación:", err);
        mostrarError("Error al procesar el archivo Excel");
      } finally {
        setImporting(false);
        setImportProgress(0);
        setImportStatus("");
        cargarOcurrencias();
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const mostrarError = (mensaje) => {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: mensaje,
      confirmButtonColor: "#8B4513",
    });
  };

  const mostrarExito = (mensaje) => {
    Swal.fire({
      icon: "success",
      title: "Éxito",
      text: mensaje,
      confirmButtonColor: "#8B4513",
    });
  };

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    const filteredOcurrencias = ocurrencias.filter((ocurrencia) => {
      return (
        (filtroPeriodo === "" || ocurrencia.periodo === filtroPeriodo) &&
        (filtroEmpleado === "" || ocurrencia.empleado_id === filtroEmpleado)
      );
    });

    return {
      total: filteredOcurrencias.length,
      aprobadas: filteredOcurrencias.filter((o) => o.activo).length,
      pendientes: filteredOcurrencias.filter((o) => !o.activo).length,
      justificadas: filteredOcurrencias.filter((o) => o.justificado).length,
    };
  };

  const estadisticas = calcularEstadisticas();

  if (loading) {
    return (
      <div
        className="min-vh-100 d-flex justify-content-center align-items-center"
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        }}
      >
        <div className="text-center">
          <Spinner
            animation="border"
            style={{ color: "#8B4513", width: "3rem", height: "3rem" }}
          />
          <p className="mt-3 mb-0" style={{ color: "#8B4513" }}>
            Cargando datos...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "95%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              borderBottom: "3px solid #D2691E",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaClipboardList className="me-3" />
                Gestión de Ocurrencias
              </h2>
              <p className="mb-0 opacity-75">
                Administra las ocurrencias manuales de los empleados
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con información */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        color: "white",
                      }}
                    >
                      <FaClipboardList size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">
                      Gestión de Ocurrencias Laborales
                    </h5>
                    <p className="text-muted mb-0 small">
                      Registra, edita y aprueba las ocurrencias de asistencia de tus empleados. Solo las ocurrencias aprobadas (activas) afectan los cálculos de planillas.
                    </p>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      <Badge
                        style={{
                          background:
                            "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                          fontSize: "0.9rem",
                          padding: "8px 16px",
                        }}
                      >
                        <FaUsers className="me-1" />
                        {empleados.length} Empleados
                      </Badge>
                      <Badge
                        style={{
                          background:
                            "linear-gradient(135deg, #B8860B 0%, #DAA520 100%)",
                          fontSize: "0.9rem",
                          padding: "8px 16px",
                        }}
                      >
                        <FaClipboardList className="me-1" />
                        {estadisticas.total} Ocurrencias
                      </Badge>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Progreso de Importación */}
            {importing && (
              <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                <Card.Body className="p-4 text-center">
                  <div
                    className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "60px",
                      height: "60px",
                      background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                      color: "white"
                    }}
                  >
                    <Spinner animation="border" variant="light" size="sm" />
                  </div>
                  <h5 className="mb-2 fw-bold" style={{ color: "#8B4513" }}>Importando Ocurrencias...</h5>
                  <p className="text-muted mb-3">{importStatus}</p>
                  <ProgressBar
                    now={importProgress}
                    label={`${importProgress}%`}
                    variant="warning"
                    animated
                    style={{ height: "20px", borderRadius: "10px" }}
                  />
                </Card.Body>
              </Card>
            )}

            {/* Formulario de filtros y acciones */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background:
                    "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <FaFilter className="me-2" />
                    <h5 className="mb-0 fw-semibold">Filtros y Acciones</h5>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-semibold"
                        style={{ color: "#8B4513" }}
                      >
                        <FaCalendarAlt className="me-1" />
                        Período de Planilla
                      </Form.Label>
                      <Form.Select
                        value={filtroPeriodo}
                        onChange={(e) => setFiltroPeriodo(e.target.value)}
                        style={{
                          borderRadius: "10px",
                          border: "2px solid #e9ecef",
                          padding: "10px",
                        }}
                      >
                        <option value="">Todos los períodos</option>
                        {[...new Set(ocurrencias.map((o) => o.periodo))].sort().reverse().map(
                          (periodo) => (
                            <option key={periodo} value={periodo}>
                              {periodo}
                            </option>
                          )
                        )}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-semibold"
                        style={{ color: "#8B4513" }}
                      >
                        <FaUsers className="me-1" />
                        Filtrar por Empleado
                      </Form.Label>
                      <Form.Select
                        value={filtroEmpleado}
                        onChange={(e) => setFiltroEmpleado(e.target.value)}
                        style={{
                          borderRadius: "10px",
                          border: "2px solid #e9ecef",
                          padding: "10px",
                        }}
                      >
                        <option value="">Todos los empleados</option>
                        {empleados.map((empleado) => (
                          <option key={empleado.id} value={empleado.id}>
                            {empleado.apellidos} {empleado.nombres}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="my-4" style={{ opacity: 0.1, borderTop: "2px solid #8B4513" }} />

                <Row className="g-3 align-items-center">
                  <Col md={12} className="d-flex flex-wrap gap-2 align-items-center">
                    <Button
                      onClick={() => {
                        setEditingOcurrencia(null);
                        resetForm();
                        setShowModal(true);
                      }}
                      className="d-flex align-items-center gap-2"
                      style={{
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        border: "none",
                        padding: "10px 20px",
                        fontWeight: "bold",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      }}
                    >
                      <FaPlus size="16px" />
                      Nueva Ocurrencia
                    </Button>

                    <Button
                      variant="outline-success"
                      onClick={handleAprobarLote}
                      className="d-flex align-items-center gap-2"
                      style={{
                        borderRadius: "10px",
                        padding: "10px 20px",
                        fontWeight: "bold",
                        border: "2px solid #28a745",
                      }}
                    >
                      <FaCheckCircle size="16px" />
                      Aprobar Lote
                    </Button>

                    <Button
                      variant="outline-warning"
                      onClick={downloadTemplate}
                      className="d-flex align-items-center gap-2"
                      style={{
                        borderRadius: "10px",
                        padding: "10px 20px",
                        fontWeight: "bold",
                        color: "#8B4513",
                        borderColor: "#ffc107",
                      }}
                    >
                      <FaDownload size="16px" />
                      Plantilla
                    </Button>

                    <Button
                      variant="outline-success"
                      onClick={exportToExcel}
                      className="d-flex align-items-center gap-2"
                      style={{
                        borderRadius: "10px",
                        padding: "10px 20px",
                        fontWeight: "bold",
                      }}
                    >
                      <FaDownload size="16px" />
                      Exportar Datos
                    </Button>

                    <div className="d-flex align-items-center gap-3 p-2 rounded-3 flex-grow-1" style={{ backgroundColor: "#f8f9fa", border: "1px dashed #dee2e6" }}>
                      <div className="text-muted small fw-bold">
                        <FaFileUpload className="me-1" />
                        IMPORTAR:
                      </div>
                      <Form.Control
                        type="file"
                        accept=".xlsx"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) importFromExcel(file);
                        }}
                        style={{
                          borderRadius: "8px",
                          border: "1px solid #ced4da",
                          fontSize: "0.85rem",
                          width: "100%",
                          cursor: "pointer",
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Estadísticas */}
            <Row className="mb-4">
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaClipboardList size={32} className="mb-2" />
                    <h4 className="fw-bold">{estadisticas.total}</h4>
                    <p className="mb-0">Total Ocurrencias</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaCheckCircle size={32} className="mb-2" />
                    <h4 className="fw-bold">{estadisticas.aprobadas}</h4>
                    <p className="mb-0">Aprobadas</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #ffc107 0%, #ffb300 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaExclamationTriangle size={32} className="mb-2" />
                    <h4 className="fw-bold">{estadisticas.pendientes}</h4>
                    <p className="mb-0">Pendientes</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px" }}
                >
                  <Card.Body
                    className="text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaThumbsUp size={32} className="mb-2" />
                    <h4 className="fw-bold">{estadisticas.justificadas}</h4>
                    <p className="mb-0">Justificadas</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Tabla de ocurrencias */}
            <Card
              className="border-0 shadow-sm mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background:
                    "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <FaClipboardList className="me-2" />
                    <h5 className="mb-0 fw-semibold">Listado de Ocurrencias</h5>
                  </div>
                  <Badge
                    bg="light"
                    text="dark"
                    style={{ fontSize: "0.9rem" }}
                  >
                    {ocurrencias.filter((ocurrencia) => {
                      return (
                        (filtroPeriodo === "" ||
                          ocurrencia.periodo === filtroPeriodo) &&
                        (filtroEmpleado === "" ||
                          ocurrencia.empleado_id === filtroEmpleado)
                      );
                    }).length}{" "}
                    ocurrencias
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                  <Table
                    responsive
                    hover
                    className="mb-0"
                    style={{ fontSize: "0.9rem" }}
                  >
                    <thead
                      style={{
                        backgroundColor: "#f8f9fa",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      <tr>
                        <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                          <FaUsers className="me-1" />
                          Empleado
                        </th>
                        <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                          Tipo
                        </th>
                        <th className="border-0 py-3 px-3 text-secondary fw-semibold">
                          <FaCalendarAlt className="me-1" />
                          Fecha
                        </th>
                        <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                          Días
                        </th>
                        <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                          Horas
                        </th>
                        <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                          Estado
                        </th>
                        <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                          Aprobación
                        </th>
                        <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ocurrencias
                        .filter((ocurrencia) => {
                          return (
                            (filtroPeriodo === "" ||
                              ocurrencia.periodo === filtroPeriodo) &&
                            (filtroEmpleado === "" ||
                              ocurrencia.empleado_id === filtroEmpleado)
                          );
                        })
                        .map((ocurrencia) => {
                          const empleado = empleados.find(
                            (e) => e.id === ocurrencia.empleado_id
                          );
                          return (
                            <tr
                              key={ocurrencia.id}
                              style={{ borderBottom: "1px solid #f1f3f4" }}
                            >
                              <td className="py-3 px-3">
                                <div className="fw-bold text-dark">
                                  {empleado
                                    ? `${empleado.apellidos} ${empleado.nombres}`
                                    : ocurrencia.empleado_id}
                                </div>
                                <small className="text-muted">
                                  DNI: {ocurrencia.empleado_id}
                                </small>
                              </td>
                              <td className="py-3 px-3">
                                {getTipoBadge(ocurrencia.tipo)}
                              </td>
                              <td className="py-3 px-3">
                                <div className="text-dark">
                                  {ocurrencia.fecha}
                                </div>
                                <small className="text-muted">
                                  {ocurrencia.periodo}
                                </small>
                              </td>
                              <td className="py-3 px-3 text-center">
                                <span className="fw-bold">
                                  {ocurrencia.dias_afectados}
                                </span>
                              </td>
                              <td className="py-3 px-3 text-center">
                                <span className="fw-bold">
                                  {ocurrencia.horas_afectadas}
                                </span>
                              </td>
                              <td className="py-3 px-3 text-center">
                                {ocurrencia.justificado ? (
                                  <Badge bg="success" className="px-2 py-1">
                                    <FaCheck className="me-1" />
                                    Justificado
                                  </Badge>
                                ) : (
                                  <Badge bg="danger" className="px-2 py-1">
                                    <FaTimes className="me-1" />
                                    No Justificado
                                  </Badge>
                                )}
                              </td>
                              <td className="py-3 px-3 text-center">
                                {ocurrencia.activo ? (
                                  <Badge bg="success" className="px-2 py-1">
                                    <FaCheckCircle className="me-1" />
                                    Aprobado
                                  </Badge>
                                ) : (
                                  <Badge bg="warning" className="px-2 py-1">
                                    <FaTimesCircle className="me-1" />
                                    Pendiente
                                  </Badge>
                                )}
                              </td>
                              <td className="py-3 px-3 text-center">
                                <div className="d-flex justify-content-center gap-1">
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip>Editar Ocurrencia</Tooltip>
                                    }
                                  >
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleEdit(ocurrencia)}
                                      style={{
                                        borderRadius: "8px",
                                        borderColor: "#8B4513",
                                        color: "#8B4513",
                                      }}
                                    >
                                      <FaEdit />
                                    </Button>
                                  </OverlayTrigger>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip>Aprobar Ocurrencia</Tooltip>
                                    }
                                  >
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      onClick={() => handleAprobar(ocurrencia)}
                                      disabled={ocurrencia.activo}
                                      style={{ borderRadius: "8px" }}
                                    >
                                      <FaThumbsUp />
                                    </Button>
                                  </OverlayTrigger>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip>Eliminar Ocurrencia</Tooltip>
                                    }
                                  >
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleDelete(ocurrencia.id)}
                                      style={{ borderRadius: "8px" }}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </OverlayTrigger>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </div>
                {ocurrencias.filter((ocurrencia) => {
                  return (
                    (filtroPeriodo === "" ||
                      ocurrencia.periodo === filtroPeriodo) &&
                    (filtroEmpleado === "" ||
                      ocurrencia.empleado_id === filtroEmpleado)
                  );
                }).length === 0 && (
                    <div className="text-center py-5">
                      <div
                        className="bg-light rounded-circle mx-auto mb-3"
                        style={{
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <FaClipboardList size={32} className="text-muted" />
                      </div>
                      <h5 className="text-muted mb-2">No hay ocurrencias registradas</h5>
                      <p className="text-muted mb-3">
                        Comienza agregando una nueva ocurrencia o ajusta los filtros.
                      </p>
                    </div>
                  )}
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>

        {/* Modal para agregar/editar ocurrencia */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header
            closeButton
            className="text-white"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
            }}
          >
            <Modal.Title>
              {editingOcurrencia ? "Editar Ocurrencia" : "Nueva Ocurrencia"}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body style={{ backgroundColor: "#f8f9fa" }}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      <FaUsers className="me-1" />
                      Empleado
                    </Form.Label>
                    <Form.Select
                      name="empleado_id"
                      value={formData.empleado_id}
                      onChange={handleInputChange}
                      required
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                      }}
                    >
                      <option value="">Seleccionar empleado</option>
                      {empleados.map((empleado) => (
                        <option key={empleado.id} value={empleado.id}>
                          {empleado.apellidos} {empleado.nombres} - {empleado.id}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      Tipo de Ocurrencia
                    </Form.Label>
                    <Form.Select
                      name="tipo"
                      value={formData.tipo}
                      onChange={handleInputChange}
                      required
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                      }}
                    >
                      <option value="">Seleccionar tipo</option>
                      {tiposOcurrencia.map((tipo) => (
                        <option key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      <FaCalendarAlt className="me-1" />
                      Fecha
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="fecha"
                      value={formData.fecha}
                      onChange={handleFechaChange}
                      required
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Período</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.periodo}
                      readOnly
                      placeholder="Se calcula automáticamente"
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                        backgroundColor: "#f8f9fa",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Días Afectados</Form.Label>
                    <Form.Control
                      type="number"
                      name="dias_afectados"
                      value={formData.dias_afectados}
                      onChange={handleDiasChange}
                      min="0"
                      step="0.5"
                      required
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Horas Afectadas</Form.Label>
                    <Form.Control
                      type="number"
                      name="horas_afectadas"
                      value={formData.horas_afectadas}
                      onChange={handleHorasChange}
                      min="0"
                      step="0.5"
                      required
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Minutos Tardanza</Form.Label>
                    <Form.Control
                      type="number"
                      name="minutos_tardanza"
                      value={formData.minutos_tardanza}
                      onChange={handleInputChange}
                      min="0"
                      required
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Con Goce de Haber"
                      name="con_goce"
                      checked={formData.con_goce}
                      onChange={handleInputChange}
                      className="fw-semibold"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Justificado"
                      name="justificado"
                      checked={formData.justificado}
                      onChange={handleInputChange}
                      className="fw-semibold"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Motivo</Form.Label>
                <Form.Control
                  type="text"
                  name="motivo"
                  value={formData.motivo}
                  onChange={handleInputChange}
                  placeholder="Describa el motivo de la ocurrencia"
                  required
                  style={{
                    borderRadius: "10px",
                    border: "2px solid #e9ecef",
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Observaciones</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleInputChange}
                  placeholder="Observaciones adicionales"
                  style={{
                    borderRadius: "10px",
                    border: "2px solid #e9ecef",
                  }}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer style={{ backgroundColor: "#f8f9fa" }}>
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                style={{ borderRadius: "10px" }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                style={{
                  borderRadius: "10px",
                  background:
                    "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                  border: "none",
                }}
              >
                {editingOcurrencia ? "Actualizar" : "Guardar"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </div>
  );
};

export default OcurrenciasMes;